// Grade calculator: try any species with any rolls and see the score, where
// it comes from, how rare it is, what it grows into, and how it fares on a hunt
// (blows to kill and one-hit odds, from the hunt's own damage formula).
import { huntPower } from '../shared/power.ts';
import { CONFIG } from '../shared/config.ts';
import { SPECIES, LINES } from '../shared/species.ts';
import { REGIONS, GROUNDS, GROUND, AREA } from '../shared/grounds.ts';
import { huntOddsTable } from './huntodds.ts';
import { STAT_NAMES, rollCreature, type Rolls } from '../shared/rolls.ts';
import { statWeights, ratingExactOf, ratingParts } from '../shared/rating.ts';
import { GRADE_CUTOFFS, gradeOf, percentileText } from '../shared/grade.ts';
import { gradeBadge } from './badges.ts';
import { computeStats } from '../shared/stats.ts';
import { REFINE_STATS, refineOf } from '../shared/refine.ts';
import { Rng, randomSeed } from '../shared/rng.ts';
import { oddsAtLeast } from '../shared/odds.ts';
import { auraOneInNow } from './auracharm.ts';
import type { CreatureDTO } from '../shared/protocol.ts';
import { spriteUrl } from './assets.ts';
import { esc, full, elChip } from './fmt.ts';
import { patch } from './dom.ts';
import { ELEMENTS, ELEMENT_IDS } from '../shared/elements.ts';
import { lineCoverageElement } from '../shared/tablets.ts';
import { psecTabs, psecWrap } from './psec.ts';

const C = {
  species: LINES[0].stages[LINES[0].stages.length - 1].id,
  level: 100,
  ivs: [16, 16, 16, 16, 16, 16] as Rolls['ivs'],
  quality: 1,
  bloodline: 0,
  aura: false,
  /** Refine steps for HP, Attack, Defense, Magic, Resist: base stat points, never part of the score. */
  refine: [0, 0, 0, 0, 0] as number[],
  /** The hunt the "On a hunt" box fights (a ground id); empty: the highest hunt at or below the level in the species' region. */
  hunt: '',
  /** A Rune Tablet's element on the Coverage slot; empty: the line's own coverage. */
  tablet: '',
};

const CALC_SECS = [{ key: 'rolls', title: 'Rolls' }, { key: 'grade', title: 'Grade' }, { key: 'stats', title: 'Stats' }, { key: 'hunt', title: 'Hunt' }];
const sec = (key: string, html: string, part = '') => psecWrap('calc', key, CALC_SECS.map((x) => x.key), html, true, part);

function calcHunt() {
  const g = GROUND[C.hunt];
  if (g) return g;
  const reg = SPECIES[C.species].region;
  const inReg = GROUNDS.filter((x) => x.region === reg);
  return [...inReg].reverse().find((x) => x.level <= C.level) ?? inReg[0] ?? GROUNDS[0];
}

const rolls = (): Rolls => ({ ivs: C.ivs, quality: C.quality, bloodline: C.bloodline, aura: C.aura, trait: '' });
const finalOf = (id: string) => { let s = SPECIES[id]; while (s.evolvesTo) s = SPECIES[s.evolvesTo]; return s; };

/** Open the calculator on a species with average rolls. */
export function calcSpecies(id: string) {
  C.species = id; C.level = 100; C.ivs = [16, 16, 16, 16, 16, 16]; C.quality = 1; C.bloodline = 0; C.aura = false; C.refine = [0, 0, 0, 0, 0]; C.tablet = '';
}

/** Open the calculator on an owned creature's rolls. */
export function loadCalc(c: CreatureDTO) {
  C.species = c.species; C.level = c.level; C.ivs = [...c.rolls.ivs] as Rolls['ivs'];
  C.quality = c.rolls.quality; C.bloodline = c.rolls.bloodline; C.aura = c.rolls.aura; C.refine = refineOf(c); C.tablet = c.tablet ?? '';
}

export function calcPane(): string {
  const w = statWeights(C.species);
  const opts = REGIONS.map((reg) => `<optgroup label="${esc(reg.name)}">${LINES.filter((l) => l.region === reg.id).flatMap((l) => l.stages)
    .map((s) => `<option value="${s.id}"${s.id === C.species ? ' selected' : ''}>${esc(s.name)}</option>`).join('')}</optgroup>`).join('');
  const q = CONFIG.quality;
  // Rolls / Grade / Stats / Hunt take turns so the calculator never scrolls (Cole, 2026-10-27); wide screens keep
  // the rolls on the left (fit.css hides that tab there). Refine sits with Stats and the coverage move with Hunt.
  return `<div class="calc">${psecTabs('calc', CALC_SECS, undefined, true)}
    <div class="calc-in panel">${sec('rolls', `
      <label class="row"><span>Species</span><select data-calc="species">${opts}</select></label>
      <label class="row"><span>Level</span><input type="number" min="1" max="2000" value="${C.level}" data-calc="level"></label>
      <h5>Talent <span class="sub">0-31</span></h5>
      <div class="calc-ivs">${STAT_NAMES.map((n, i) => `<span>${n}</span>
        <input type="number" min="0" max="31" value="${C.ivs[i]}" data-calc="iv" data-i="${i}" aria-label="${n} talent">
        <span class="bar" title="${Math.round(w[i] * 100)}% of the talent score"><i style="width:${w[i] * 100 * 3}%"></i></span>
        <span class="sub">${w[i] ? Math.round(w[i] * 100) + '%' : 'unused'}</span>`).join('')}</div>
      <div class="actions"><button class="btn tiny" data-act="calc-iv" data-id="31">All 31</button><button class="btn tiny" data-act="calc-iv" data-id="16">All 16</button><button class="btn tiny" data-act="calc-iv" data-id="0">All 0</button><button class="btn tiny" data-act="calc-random">Random catch</button></div>
      <h5>Quality <span class="sub" id="calc-qv">×${C.quality.toFixed(3)}</span></h5>
      <input type="range" min="${q.min}" max="${q.max}" step="0.001" value="${C.quality}" data-calc="quality" aria-label="Quality" style="width:100%">
      <h5>Bloodline</h5>
      <div class="seg">${CONFIG.bloodline.map((b, i) => `<button class="btn tiny${i === C.bloodline ? ' on' : ''}" data-act="calc-bl" data-id="${i}" title="${b.pct}% of catches, stats ×${b.mult}">${b.name}</button>`).join('')}</div>
      <label class="row" style="margin-top:8px"><input type="checkbox" data-calc="aura"${C.aura ? ' checked' : ''}> <span>Aura (1 in ${full(auraOneInNow())} catches, stats ×${CONFIG.auraMult})</span></label>`)}
    </div>
    <div class="calc-out panel" id="calc-out">${calcOut()}</div>
  </div>`;
}

/** The line's own coverage element, then every element a Rune Tablet can set (not one of the species' own). */
function coverageOpts(): string {
  const own = lineCoverageElement(C.species), sp = SPECIES[C.species];
  const els = ELEMENT_IDS.filter((e) => e !== own && !sp.elements.includes(e));
  return `<option value=""${C.tablet ? '' : ' selected'}>${ELEMENTS[own].name} (its own)</option>${els.map((e) => `<option value="${e}"${C.tablet === e ? ' selected' : ''}>${ELEMENTS[e].name} (Rune Tablet)</option>`).join('')}`;
}

/** The five grades on a 0-60 strip with a marker at the score (anything past 60 is S+ anyway). */
function gradeScale(score: number) {
  const top = 60;
  const bands = [...GRADE_CUTOFFS].reverse().map(([g, min], i, all) => ({ g, min, max: i + 1 < all.length ? all[i + 1][1] : top }));
  return `<div class="gradescale" style="grid-template-columns:${bands.map((b) => `${b.max - b.min}fr`).join(' ')}">${bands.map((b) => `<span class="gs ${b.g === 'S+' ? 'sp' : b.g.toLowerCase()}${gradeOf(Math.round(score)) === b.g ? ' on' : ''}">${b.g} <small>${b.min}${b.g === 'S+' ? '+' : `-${b.max - 1}`}</small></span>`).join('')}<b style="left:${(Math.min(top, score) / top) * 100}%"></b></div>`;
}

function calcOut(): string {
  const sp = SPECIES[C.species];
  const r = rolls();
  const exact = ratingExactOf(r, C.species);
  const parts = ratingParts(r, C.species);
  const P = CONFIG.ratingPoints;
  const st = computeStats(C.species, C.level, r, 0, C.refine);
  const fin = finalOf(C.species);
  const finLv = Math.max(100, C.level);
  const fst = computeStats(fin.id, finLv, r, 0, C.refine);
  const odds = oddsAtLeast(C.species, exact - parts.stage, auraOneInNow());
  const statRow = (s: typeof st) => [s.hp, s.might, s.guard, s.focus, s.will, s.swift];
  const part = (label: string, v: number, max: number) =>
    `<span>${label}</span><span class="bar"><i style="width:${(v / max) * 100}%"></i></span><span>${v.toFixed(1)} <span class="sub">of ${max}</span></span>`;
  return `${sec('grade', `
    <div class="calc-head"><img src="${spriteUrl(C.species, C.aura)}" alt="">
      ${gradeBadge(Math.round(exact), 'lg')}
      <div><div class="calc-score">Grade ${gradeOf(Math.round(exact))} · ${exact.toFixed(1)}<span class="sub"> / 100</span></div>
      <div class="sub">${percentileText(Math.round(exact), sp.stage)}</div></div></div>
    ${gradeScale(exact)}
    <h5>What makes the score</h5>
    <div class="calc-parts">${part('Talent', parts.talent, P.talent)}${part('Quality', parts.quality, P.quality)}${part('Bloodline', parts.bloodline, CONFIG.bloodlineRating[CONFIG.bloodlineRating.length - 1])}${part('Aura', parts.aura, P.aura)}${parts.stage ? part('Ascended', parts.stage, CONFIG.ratingPerStage * 2) : ''}</div>
    ${fin.id !== sp.id ? `<p class="sub">As ${esc(fin.name)}: ${ratingExactOf(r, fin.id).toFixed(1)}</p>` : ''}
    <h5>How rare</h5>
    <p>${odds >= 0.5 ? 'About half of all catches roll this well or better.' : odds > 0 ? `About <b>1 in ${oneIn(odds)}</b> ${esc(sp.name)} catches rolls this score or better.` : 'No catch can roll this score.'}</p>
    `)}${sec('stats', `<h5>Refine <span class="sub">base stat points, not part of the score</span></h5>
      <div class="calc-rf">${REFINE_STATS.map((i) => `<label>${STAT_NAMES[i]}<input type="number" min="0" max="99" value="${C.refine[i]}" data-calc="refine" data-i="${i}" aria-label="${STAT_NAMES[i]} refine"></label>`).join('')}</div>
    <h5>Stats${C.refine.some((n) => n) ? ` <span class="sub">with refine +${C.refine.reduce((a, b) => a + b, 0)}</span>` : ''}</h5>
    <table class="calc-st"><tr><th></th><th>${esc(sp.name)} Lv ${C.level}</th>${fin.id !== sp.id || finLv !== C.level ? `<th>${esc(fin.name)} Lv ${finLv}</th>` : ''}</tr>
      ${STAT_NAMES.map((n, i) => `<tr><td>${n}</td><td>${full(statRow(st)[i])}</td>${fin.id !== sp.id || finLv !== C.level ? `<td>${full(statRow(fst)[i])}</td>` : ''}</tr>`).join('')}
      <tr><td><b>Power</b></td><td><b>${full(huntPower({ species: C.species, level: C.level, rolls: r, stars: 0, relic: null, refine: C.refine }))}</b></td>${fin.id !== sp.id || finLv !== C.level ? `<td><b>${full(huntPower({ species: fin.id, level: finLv, rolls: r, stars: 0, relic: null, refine: C.refine }))}</b></td>` : ''}</tr></table>
    <p class="sub">${sp.elements.map(elChip).join(' ')} ${sp.rarity}, attacks with ${sp.style === 'might' ? 'Attack (Magic unused)' : 'Magic (Attack unused)'}</p>`)}
    ${sec('hunt', huntBox(st))}`;
}

/** Blows to kill and one-hit odds against a hunt's average wild, at the calculator's level and rolls. */
function huntBox(st: ReturnType<typeof computeStats>): string {
  const g = calcHunt();
  const opts = REGIONS.map((reg) => `<optgroup label="${esc(reg.name)}">${GROUNDS.filter((x) => x.region === reg.id)
    .map((x) => `<option value="${x.id}"${x.id === g.id ? ' selected' : ''}>Lv ${x.level} · ${esc(SPECIES[x.species].name)} (${esc(AREA[x.area].name)})</option>`).join('')}</optgroup>`).join('');
  return `<h5>On a hunt</h5>
    <label class="row"><span>Hunt</span><select data-calc="hunt">${opts}</select></label>
    <label class="row"><span>Coverage</span><select data-calc="tablet" aria-label="Coverage element">${coverageOpts()}</select></label>
    ${huntOddsTable({ speciesId: C.species, level: C.level, stats: st, trait: '', ...(C.tablet ? { tablet: C.tablet } : {}) }, g)}`;
}

/** "1 in 3,400" for everyday odds, "1 in 2.3 × 10^21" past a trillion. */
function oneIn(odds: number): string {
  const n = Math.max(1, 1 / odds);
  if (n < 1e12) return full(n);
  const e = Math.floor(Math.log10(n));
  return `${(n / 10 ** e).toFixed(1)} × 10<sup>${e}</sup>`;
}


/** Live edits: refresh only the results so the field being typed in keeps focus. */
export function calcInput(el: HTMLInputElement | HTMLSelectElement): boolean {
  const k = el.dataset.calc;
  if (!k) return false;
  if (k === 'species') { C.species = el.value; C.hunt = ''; if (C.tablet && (SPECIES[C.species].elements.includes(C.tablet as never) || C.tablet === lineCoverageElement(C.species))) C.tablet = ''; return true; }
  if (k === 'tablet') C.tablet = el.value;
  if (k === 'hunt') C.hunt = el.value;
  if (k === 'level') { C.level = Math.max(1, Math.min(2000, Math.round(Number(el.value) || 1))); C.hunt = ''; }
  if (k === 'iv') C.ivs[Number(el.dataset.i)] = Math.max(0, Math.min(31, Math.round(Number(el.value) || 0)));
  if (k === 'quality') { C.quality = Number(el.value); const qv = document.getElementById('calc-qv'); if (qv) qv.textContent = `×${C.quality.toFixed(3)}`; }
  if (k === 'aura') C.aura = (el as HTMLInputElement).checked;
  if (k === 'refine') C.refine[Number(el.dataset.i)] = Math.max(0, Math.min(99, Math.round(Number(el.value) || 0)));
  const out = document.getElementById('calc-out');
  patch(out, calcOut());
  return false;
}

/** Button actions; returns true when the whole pane should be redrawn. */
export function calcAct(act: string, id: string): boolean {
  if (act === 'calc-iv') { C.ivs = Array(6).fill(Number(id)) as Rolls['ivs']; return true; }
  if (act === 'calc-bl') { C.bloodline = Number(id); return true; }
  if (act === 'calc-random') {
    const r = rollCreature(new Rng(randomSeed()));
    C.ivs = r.ivs; C.quality = r.quality; C.bloodline = r.bloodline; C.aura = r.aura;
    return true;
  }
  return false;
}
